<script>
// import BulkInventoryForm from '@src/forms/BulkInventory'
import Request from '@src/mixins/request'
import dropdown from '@src/data/dropdown.json'

const initConditions = {
  dateFrom: null,
  dateTo: null,
  roomTypes: [],
  days: dropdown.dayOfWeek.map((d) => d.value),
}

const initForm = {
  available: null,
  rate: null,
  stopSell: null,
  cta: null,
}

export default {
  $_veeValidate: { validator: 'new' },

  mixins: [Request],

  props: {
    roomTypes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      dialog: false,
      color: 'gold',
      conditions: Object.assign({}, initConditions),
      form: Object.assign({}, initForm),
      schema: {
        // date: 'required|date_format:yyyy-MM-dd',
        rate: 'min_value:1',
        available: 'min_value:0',
        // bool: 'required',
      },
      selected: {
        available: false,
        rate: false,
        stopSell: false,
        cta: false,
        ctd: false,
      },
      dropdown,
    }
  },

  watch: {
    dialog(val) {
      if (!val) {
        setTimeout(() => {
          this.resetAll()
        }, 300)
      }
    },
  },

  methods: {
    resetField(field, value) {
      if (!value) {
        this.form[field] = initForm[field]
      }
    },
    resetForm(field = null) {
      Object.assign(this.form, initForm)
    },
    resetConditions() {
      Object.assign(this.conditions, initConditions)
    },
    resetSelected() {
      Object.keys(this.selected).forEach((key) => {
        this.selected[key] = false
      })
    },
    resetAll() {
      this.resetForm()
      this.resetConditions()
      this.resetSelected()
    },
    update() {
      const updatePayload = {}
      Object.keys(this.selected).forEach((key) => {
        const isUpdated = this.selected[key]
        if (isUpdated) updatePayload[key] = this.form[key]
      })
      const data = Object.assign({}, this.conditions, { update: updatePayload })
      this.updateData({
        url: this.endpoint.inventory.bulkUpdate,
        data,
      }).then((result) => {
        if (result) this.dialog = false
      })
    },
  },
}
</script>

<template>
  <VDialog v-model="dialog" max-width="560px" persistent>
    <slot slot="activator" name="activator" />

    <VForm>
      <VCard>
        <VCardTitle class="headline" :class="`${color}--text`">
          Bulk Update
        </VCardTitle>

        <VCardText>
          <VContainer class="pa-0" grid-list-lg>
            <VLayout row wrap>
              <!-- Conditions -->
              <VFlex xs12 sm6>
                <div class="title mb-2">Conditions</div>

                <BaseDatePicker
                  v-model="conditions.dateFrom"
                  v-validate="schema.dateFrom"
                  data-vv-name="from"
                  label="From"
                  hide-details
                  :error-messages="errors.collect('from')"
                  :color="color"
                />

                <BaseDatePicker
                  v-model="conditions.dateTo"
                  v-validate="schema.dateTo"
                  data-vv-name="to"
                  label="To"
                  hide-details
                  :error-messages="errors.collect('to')"
                  :color="color"
                />

                <VSelect
                  v-model="conditions.days"
                  label="On days"
                  :items="dropdown.dayOfWeek"
                  :color="color"
                  multiple
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="grey--text caption">
                      {{ conditions.days.length }} selected
                    </span>
                  </template>
                </VSelect>

                <VSelect
                  v-model="conditions.roomTypes"
                  label="Room Types"
                  item-text="name"
                  item-value="id"
                  :items="roomTypes"
                  :color="color"
                  multiple
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="grey--text caption">
                      {{ conditions.roomTypes.length }} selected
                    </span>
                  </template>
                </VSelect>
              </VFlex>
              <!-- End of Conditions -->

              <!-- Update -->
              <VFlex xs12 sm6>
                <div class="title mb-2">Update</div>

                <VLayout align-center>
                  <VCheckbox
                    v-model="selected.available"
                    class="shrink mr-2"
                    :color="color"
                    hide-details
                    @change="resetField('available', $event)"
                  />
                  <VTextField
                    v-model="form.available"
                    v-validate="schema.available"
                    label="Available"
                    data-vv-name="available"
                    :error-messages="errors.collect('available')"
                    :disabled="!selected.available"
                    :color="color"
                    :hide-details="!errors.has('available')"
                  />
                </VLayout>

                <VLayout align-center>
                  <VCheckbox
                    v-model="selected.rate"
                    v-validate="schema.rate"
                    class="shrink mr-2"
                    data-vv-name="rate"
                    :error-messages="errors.collect('rate')"
                    :color="color"
                    hide-details
                    @change="resetField('rate', $event)"
                  />
                  <VTextField
                    v-model="form.rate"
                    label="Rate"
                    :disabled="!selected.rate"
                    :color="color"
                    :hide-details="!errors.has('rate')"
                  />
                </VLayout>

                <VLayout align-center>
                  <VCheckbox
                    v-model="selected.stopSell"
                    class="shrink mr-2"
                    :color="color"
                    hide-details
                    @change="resetField('stopSell', $event)"
                  />
                  <VSelect
                    v-model="form.stopSell"
                    label="Stop Sell"
                    :items="dropdown.switch"
                    :disabled="!selected.stopSell"
                    :color="color"
                    hide-details
                  />
                </VLayout>

                <VLayout align-center>
                  <VCheckbox
                    v-model="selected.cta"
                    class="shrink mr-2"
                    :color="color"
                    hide-details
                    @change="resetField('cta', $event)"
                  />
                  <VSelect
                    v-model="form.cta"
                    label="CTA"
                    :items="dropdown.switch"
                    :disabled="!selected.cta"
                    :color="color"
                    hide-details
                  />
                </VLayout>

                <VLayout align-center>
                  <VCheckbox
                    v-model="selected.ctd"
                    class="shrink mr-2"
                    :color="color"
                    hide-details
                    @change="resetField('ctd', $event)"
                  />
                  <VSelect
                    v-model="form.ctd"
                    label="CTD"
                    :items="dropdown.switch"
                    :disabled="!selected.ctd"
                    :color="color"
                    hide-details
                  />
                </VLayout>
              </VFlex>
              <!-- End of Update -->
            </VLayout>
          </VContainer>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn depressed :disabled="loading" @click="dialog = false">
            Cancel
          </VBtn>

          <VBtn
            dark
            depressed
            :color="color"
            :loading="loading"
            @click="update"
          >
            Update
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
